/**
 * HVP Cascade Controller - pre-selected + browser-restored values
 *
 * Adopts values that arrive without change events: WooCommerce defaults at
 * init, and back/forward restoration on pageshow (0/400/1500ms burst). Both
 * run the same tail: loadBatchOptions -> autoSelectSingleOptions ->
 * findMatchingVariation -> UI -> emitSelectionChanged.
 *
 * Split out of cascade-controller.js (350-line rule): the methods below moved
 * VERBATIM and are installed on HVPCascadeController.prototype by
 * cascade-controller.js, so `this` is the same instance and behavior is identical.
 *
 * @package HaruVariationsPro
 * @version 1.0.0
 */

export const CascadeRestoreMethods = {
    /**
     * Initialize controller state from pre-selected dropdown values
     *
     * WooCommerce may set default variation values before HVP initializes.
     * This method reads those pre-existing values and updates internal state
     * so that UI effects (highlighting, readonly styling) are applied correctly
     * on page load without requiring user interaction.
     *
     * @returns {Promise<void>}
     */
    async initializePreSelectedValues() {
        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] ----------------------------------------');
        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] CHECKING FOR PRE-SELECTED DEFAULT VALUES');
        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] ----------------------------------------');

        let preSelectedCount = 0;

        // Scan all dropdowns for pre-existing values set by WooCommerce
        this.dropdowns.forEach((select, attrName) => {
            const currentValue = select.value;
            const optionCount = Array.from(select.options).filter(opt => opt.value !== '').length;

            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Checking dropdown:', attrName, '| value:', currentValue || '(empty)', '| options:', optionCount);

            // Check if dropdown has a pre-selected value (not empty/placeholder)
            if (currentValue && currentValue !== '') {
                // Only update if we don't already have this selection
                // (autoSelectSingleOptions may have already set some)
                if (!this.selections[attrName]) {
                    this.selections[attrName] = currentValue;
                    preSelectedCount++;

                    window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Found pre-selected value:', attrName, '=', currentValue);
                } else {
                    window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Selection already exists for:', attrName, '=', this.selections[attrName]);
                }

                // ALWAYS ensure correct styling for dropdowns with values
                // Remove empty class since we have a value
                select.classList.remove('hvp-empty');

                // Enable dropdown if it was incorrectly disabled
                if (select.disabled && optionCount > 0) {
                    select.disabled = false;
                    window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Re-enabled dropdown:', attrName);
                }

                // Apply single-option styling if this is a single-option dropdown
                if (optionCount === 1) {
                    select.classList.add('hvp-single-option', 'hvp-auto-selected');
                    window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Marked as single-option:', attrName);
                }
            }
        });

        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Total selections after init:', JSON.stringify(this.selections));
        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] New pre-selected values found:', preSelectedCount);

        if (preSelectedCount > 0 || Object.keys(this.selections).length > 0) {
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Complete selections state:', JSON.stringify(this.selections));

            // FIX: Always load batch options to filter dropdowns, even if all selections are complete
            // This ensures turnaround and other attributes are properly filtered on page load
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Loading batch options to filter all dropdowns based on pre-selections...');
            await this.loadBatchOptions();

            // Try auto-selecting singles after loading
            await this.autoSelectSingleOptions();

            // If we have complete selections, find the matching variation
            if (this.areAllSelectionsComplete()) {
                window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] All selections complete - finding variation');
                await this.findMatchingVariation();
            }
        } else {
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] No pre-selected values found');
        }

        // IMPORTANT: Force immediate UI update to apply visual effects
        // Don't just schedule - execute directly to ensure page load shows effects
        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Forcing immediate UI update for initial styling...');
        this.uiManager.updateAllHighlighting();
        this.uiManager.updateProgressIndicator(this.dropdowns);
    },

    /**
     * pageshow → reconcile browser-restored dropdown values.
     *
     * Fires on every navigation (persisted=true for bfcache, where the JS heap
     * came back with the DOM so the passes below are no-ops). Restoration
     * timing varies per engine, so run a burst of passes; each is a cheap diff
     * that returns immediately when DOM and state already agree.
     */
    handlePageShow() {
        this.restoreReconcileTimers.forEach(clearTimeout);
        this.restoreReconcileTimers = [];

        this.reconcileRestoredSelections('pageshow').catch(() => {});
        [400, 1500].forEach(delay => {
            this.restoreReconcileTimers.push(setTimeout(() => {
                this.reconcileRestoredSelections('pageshow+' + delay).catch(() => {});
            }, delay));
        });
    },

    /**
     * Adopt dropdown values the browser restored without change events.
     *
     * On back/forward, engines refill <select>s from session history silently;
     * any value that lands after init never enters this.selections, so
     * areAllSelectionsComplete() can never be true and findMatchingVariation()
     * never runs — the visually-selected value is a ghost. DOM wins here: what
     * the customer SEES selected must be what the cascade computes with. After
     * adopting, run the same tail as initializePreSelectedValues so options
     * filter, singles auto-select, a complete set resolves its variation, and
     * listeners (theme lock, smart-hide, coachmarks, RPS) re-evaluate.
     *
     * @param {string} reason - Trigger label for logging.
     * @returns {Promise<void>}
     */
    async reconcileRestoredSelections(reason) {
        if (!this.initialized || this.isReconcilingRestore) {
            return;
        }

        const adopted = {};
        this.dropdowns.forEach((select, attrName) => {
            const value = select.value;
            if (value && this.selections[attrName] !== value) {
                adopted[attrName] = value;
            }
        });

        const attrs = Object.keys(adopted);
        if (attrs.length === 0) {
            return;
        }

        this.isReconcilingRestore = true;
        this.log('Reconciling browser-restored selections (' + reason + '):', adopted);
        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] RESTORE RECONCILE (' + reason + '):', JSON.stringify(adopted));

        try {
            Object.assign(this.selections, adopted);
            attrs.forEach(attr => {
                const select = this.dropdowns.get(attr);
                if (select) {
                    select.classList.remove('hvp-empty');
                }
            });

            await this.loadBatchOptions();
            await this.autoSelectSingleOptions();

            if (this.areAllSelectionsComplete()) {
                await this.findMatchingVariation();
            }

            this.uiManager.updateAllHighlighting();
            this.uiManager.updateProgressIndicator(this.dropdowns);

            const last = attrs[attrs.length - 1];
            this.emitSelectionChanged(last, adopted[last]);
        } finally {
            this.isReconcilingRestore = false;
        }
    },
};

export default CascadeRestoreMethods;
